export class ApiError extends Error {
  constructor(public status: number, message: string, public code?: string) {
    super(message);
  }
}

const pendingReads = new Set<AbortController>();

/** Una navigazione annulla solo le letture: salvataggi e upload devono proseguire. */
export function cancelPendingReads(): void {
  for (const controller of pendingReads) controller.abort();
  pendingReads.clear();
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const controller = method === 'GET' ? new AbortController() : null;
  if (controller) pendingReads.add(controller);
  try {
    const res = await fetch(url, {
      method,
      headers: { ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(url.startsWith('/api/family/') ? { 'X-Memorie-Atlas': '2' } : {}) },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller?.signal,
    });
    if (!res.ok) {
      const data = await res.json().catch(() => null) as { error?: string } | null;
      throw new ApiError(res.status, `${method} ${url} → ${res.status}`, data?.error);
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  } finally {
    if (controller) pendingReads.delete(controller);
  }
}

export const api = {
  get: <T>(url: string) => request<T>('GET', url),
  post: <T>(url: string, body?: unknown) => request<T>('POST', url, body),
  put: <T>(url: string, body?: unknown) => request<T>('PUT', url, body),
  del: <T>(url: string) => request<T>('DELETE', url),
};

/** POST che risponde in SSE: invoca i callback per ogni evento ricevuto.
 *  Opzioni: signal per annullare (chiude la connessione: il server se ne accorge),
 *  timeoutMs sull'intera risposta. Senza opzioni il comportamento è quello storico
 *  (nessun limite di tempo). I commenti SSE (":ping") vengono ignorati. */
export async function postSse(
  url: string,
  body: unknown,
  handlers: Record<string, (data: Record<string, unknown>) => void>,
  opts: { signal?: AbortSignal; timeoutMs?: number } = {},
): Promise<void> {
  const ctrl = new AbortController();
  const onAbort = (): void => ctrl.abort(opts.signal?.reason);
  if (opts.signal?.aborted) onAbort();
  else opts.signal?.addEventListener('abort', onAbort, { once: true });
  // Timeout di INATTIVITÀ: si riarma a ogni pezzo ricevuto (anche i commenti «:ping»), così un
  // turno lungo ma vivo non viene interrotto mentre il server sta ancora lavorando.
  let timer = opts.timeoutMs
    ? window.setTimeout(() => ctrl.abort(new Error('tempo scaduto')), opts.timeoutMs)
    : null;
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: ctrl.signal,
    });
    if (!res.ok || !res.body) throw new ApiError(res.status, `SSE ${url} → ${res.status}`);
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (timer !== null && opts.timeoutMs) {
        clearTimeout(timer);
        timer = window.setTimeout(() => ctrl.abort(new Error('tempo scaduto')), opts.timeoutMs);
      }
      buffer += decoder.decode(value, { stream: true });
      let idx;
      while ((idx = buffer.indexOf('\n\n')) >= 0) {
        const frame = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        let event = 'message';
        let data = '';
        for (const line of frame.split('\n')) {
          if (line.startsWith('event: ')) event = line.slice(7).trim();
          else if (line.startsWith('data: ')) data += line.slice(6);
        }
        if (data) {
          try {
            handlers[event]?.(JSON.parse(data) as Record<string, unknown>);
          } catch {
            /* frame malformato: ignora */
          }
        }
      }
    }
  } finally {
    if (timer !== null) clearTimeout(timer);
    opts.signal?.removeEventListener('abort', onAbort);
  }
}
